import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useStore } from "jotai";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/flexkit/Button";
import type { PropsWithClassName } from "@/components/podkit/lib/cn";
import { useOnboardingCompletion } from "@/hooks/use-onboarding-completion";
import { keys as userQueryKeys, type PlainUser } from "@/queries/user-queries";
import {
    clearOnboardingStateFromLocalStorage,
    savePendingSubmissionToLocalStorage,
} from "@/routes/ona-onboarding/local-storage";
import { skipOnboardingPromptInputSubmissionAtom } from "@/routes/ona-onboarding/onboarding-state";

type SkipOnboardingButtonProps = PropsWithClassName & {
    trackingId?: string;
};

const SkipOnboardingButton: FC<SkipOnboardingButtonProps> = ({ trackingId = "skip-onboarding-button" }) => {
    const queryClient = useQueryClient();
    const onboardingCompletion = useOnboardingCompletion();
    const store = useStore();
    const navigate = useNavigate();

    const { mutate: skipOnboarding } = useMutation({
        mutationFn: async () => {
            const user = queryClient.getQueryData<PlainUser>(userQueryKeys.getAuthenticatedUserQueryKey());
            const submission = store.get(skipOnboardingPromptInputSubmissionAtom);
            if (submission && user) {
                savePendingSubmissionToLocalStorage(submission, user.organizationId);
            }
            clearOnboardingStateFromLocalStorage();
            await onboardingCompletion.markOnboardingAsComplete();
        },
        // instead of waiting for the mutation to complete, we do the mutation in the background
        // to allow the user to leave the onboarding flow as quickly as possible without any more waiting.
        onMutate: () => {
            // the flag may be used by routes that redirect users to onboarding to *not* do so
            // in case the other async heuristics are out of date when they decide whether to redirect
            navigate("/ai", { replace: true, state: { isOnboardingSkipped: true } });
        },
    });

    return (
        <Button
            type="button"
            value="skip"
            variant="ghost"
            size="lg"
            onClick={() => {
                skipOnboarding();
            }}
            data-tracking-id={trackingId}
        >
            Skip for now
        </Button>
    );
};

export default SkipOnboardingButton;
